import { useCallback, useSyncExternalStore } from "react";
import { matchesAssetBrowseFilters, type AssetBrowseCandidate, type AssetBrowseFilters } from "./assetBrowseFilters";

/**
 * Creation-container parameters shared by the image, video, and audio
 * workspaces. The container itself is one component; only these parameters
 * change when the creator switches workflow, and each workflow keeps its own
 * selection so switching back restores what the creator had set up.
 */
export type CreationWorkflow = "image" | "video" | "audio" | "3d";
export type CreationMediaType = "image" | "video" | "audio" | "3d";
export type CreationView = "row" | "original" | "square";

export const CREATION_MEDIA_TYPES: readonly CreationMediaType[] = [
  "image",
  "video",
  "audio",
  "3d",
];

export interface CreationFilters {
  /** Media types shown in the container. Never empty — falls back to the workflow media. */
  media: readonly CreationMediaType[];
  favoritesOnly: boolean;
  view: CreationView;
  search: string;
  browse: AssetBrowseFilters;
}

export function defaultCreationFilters(
  workflow: CreationWorkflow,
): CreationFilters {
  return {
    media: [workflow],
    favoritesOnly: false,
    view: "original",
    search: "",
    browse: {},
  };
}

/**
 * Module-level so the parameters outlive any single composer mount. Switching
 * media hides one composer and shows another; without this the container would
 * come back with its filters reset, which reads as a reload.
 */
const store = new Map<CreationWorkflow, CreationFilters>();
const listeners = new Set<() => void>();

function read(workflow: CreationWorkflow): CreationFilters {
  const existing = store.get(workflow);
  if (existing) return existing;
  const initial = defaultCreationFilters(workflow);
  store.set(workflow, initial);
  return initial;
}

function write(workflow: CreationWorkflow, next: CreationFilters): void {
  store.set(workflow, next);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam — resets every workflow back to its defaults. */
export function resetCreationFilters(): void {
  store.clear();
  for (const listener of listeners) listener();
}

export interface CreationFilterControls {
  filters: CreationFilters;
  setMedia: (media: readonly CreationMediaType[]) => void;
  toggleMedia: (media: CreationMediaType) => void;
  setFavoritesOnly: (value: boolean) => void;
  setView: (value: CreationView) => void;
  setSearch: (value: string) => void;
  setBrowse: (value: AssetBrowseFilters) => void;
  clearFilters: () => void;
}

export function useCreationFilters(
  workflow: CreationWorkflow,
): CreationFilterControls {
  const filters = useSyncExternalStore(
    subscribe,
    () => read(workflow),
    () => read(workflow),
  );

  const patch = useCallback(
    (next: Partial<CreationFilters>) => {
      write(workflow, { ...read(workflow), ...next });
    },
    [workflow],
  );

  const setMedia = useCallback(
    (media: readonly CreationMediaType[]) => {
      const ordered = CREATION_MEDIA_TYPES.filter((type) =>
        media.includes(type),
      );
      patch({ media: ordered.length > 0 ? ordered : [workflow] });
    },
    [patch, workflow],
  );

  const toggleMedia = useCallback(
    (media: CreationMediaType) => {
      const current = read(workflow).media;
      setMedia(
        current.includes(media)
          ? current.filter((type) => type !== media)
          : [...current, media],
      );
    },
    [setMedia, workflow],
  );

  return {
    filters,
    setMedia,
    toggleMedia,
    setFavoritesOnly: useCallback(
      (value: boolean) => patch({ favoritesOnly: value, browse: { ...read(workflow).browse, unfavorited: undefined } }),
      [patch, workflow],
    ),
    setView: useCallback((value: CreationView) => patch({ view: value }), [
      patch,
    ]),
    setSearch: useCallback((value: string) => patch({ search: value }), [patch]),
    setBrowse: useCallback(
      (value: AssetBrowseFilters) => patch({ browse: value }),
      [patch],
    ),
    clearFilters: useCallback(
      () => patch({ browse: {}, favoritesOnly: false }),
      [patch],
    ),
  };
}

export interface CreationFilterCandidate extends AssetBrowseCandidate {
  media_type?: string | null;
  name?: string | null;
}

/** Single predicate so every workflow filters its creations identically. */
export function matchesCreationFilters(
  filters: CreationFilters,
  candidate: CreationFilterCandidate,
): boolean {
  const search = filters.search.trim().toLocaleLowerCase();
  return (
    filters.media.includes(candidate.media_type as CreationMediaType) &&
    (!filters.favoritesOnly || Boolean(candidate.is_favorite)) &&
    matchesAssetBrowseFilters(candidate, filters.browse) &&
    (!search ||
      (candidate.name ?? "").toLocaleLowerCase().includes(search))
  );
}
